import React from "react";
import { Badge } from "@/components/ui/badge";
import { XIcon, LockIcon } from "lucide-react";
import { SocialSvg } from "@/components/SocialSvg";
import { SocialMentionData } from "@/services/bedrock/dtos/Post";
import { socialNames } from "@/interfaces/dtos/SocialNetwork";
import { Tooltip } from "@/components/ui/tooltip";

interface SocialMentionBadgeProps {
  mention: SocialMentionData;
  onRemove?: () => void;
  isLocked?: boolean;
  readOnly?: boolean;
}

export function SocialMentionBadge({
  mention,
  onRemove,
  isLocked = false,
  readOnly = isLocked,
}: SocialMentionBadgeProps) {
  const openPublicLink = () => {
    if (mention.publicLink) {
      window.open(mention.publicLink, "_blank", "noopener,noreferrer");
    }
  };

  const badge = (
    <Badge
      variant="outline"
      className={`py-2.5 gap-2 bg-white border-slate-200 group transition-colors "cursor-pointer hover:border-primary/30"`}
      onClick={openPublicLink}
    >
      <SocialSvg
        networkId={mention.socialNetworkId}
        bgColorClass="bg-slate-100"
        iconColorClass="text-slate-500"
        size="xs"
      />
      <span className="text-xs font-medium text-slate-700">{mention.name}</span>
      {isLocked ? (
        <LockIcon className="size-3 text-slate-300" />
      ) : onRemove ? (
        <button
          disabled={readOnly || isLocked}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          className="hover:text-destructive text-slate-400 transition-colors"
        >
          <XIcon className="size-3" />
        </button>
      ) : undefined}
    </Badge>
  );

  if (isLocked || readOnly) {
    const tooltipText = readOnly
      ? "You do not have permission to remove this mention."
      : `Caption editing is restricted by ${
          socialNames[mention.socialNetworkId] || mention.socialNetworkId
        }. Mentions cannot be removed once posted.`;

    return (
      <Tooltip>
        <Tooltip.Trigger>{badge}</Tooltip.Trigger>
        <Tooltip.Content side="top" className="max-w-[200px] text-center">
          {tooltipText}
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return badge;
}
